import {
  HttpClient,
  HttpContext,
  HttpContextToken,
  HttpErrorResponse,
  HttpHeaders,
  HttpInterceptorFn,
  HttpParams,
  HttpResponse,
} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { catchError, from, Observable, switchMap, tap, throwError } from 'rxjs';

import { APP_MESSAGES } from '@app/core/constants/app-messages';
import {
  DashboardTypeItem,
  DashboardTypesService,
} from '@app/core/services/dashboard-types.service';
import { NotificationService } from '@app/core/services/notification.service';
import { AuthSessionService } from '@core/auth/services/auth-session.service';
import { UserDetailsService } from '@core/services/user-details.service';
import { environment } from '@env/environment';

const API_ROUTE_PREFIXES = {
  powerBi: '/api/powerbi',
  serviceNow: '/api/servicenow',
  serviceNowDirect: '/api/isgi',
  proxy: '/api/proxy'
} as const;

const SERVICE_NOW_ROUTE_PREFIXES = [
  API_ROUTE_PREFIXES.serviceNow,
  API_ROUTE_PREFIXES.serviceNowDirect,
  API_ROUTE_PREFIXES.proxy
] as const;
const COCKPIT_USER_HEADER_PATHS = [
  '/api/proxy/GetCockpitSummary',
  '/api/proxy/GetCockpitDrilldown',
  '/api/proxy/CreateRecordInsight',
] as const;

interface SuccessNotificationOptions {
  enabled: boolean;
  title?: string;
  message?: string;
}

interface PowerBiRequestConfig {
  baseUrl: string;
  exportUrl: string;
  subscriptionKey: string;
}

export const SUCCESS_NOTIFICATION_OPTIONS = new HttpContextToken<SuccessNotificationOptions>(
  () => ({
    enabled: false,
  }),
);
export const ERROR_NOTIFICATION_ENABLED = new HttpContextToken<boolean>(() => true);

export interface HttpRequestOptions {
  headers?: HttpHeaders | Record<string, string | string[]>;
  params?:
    | HttpParams
    | Record<string, string | number | boolean | readonly (string | number | boolean)[]>;
  observe?: 'body' | 'response';
  responseType?: 'blob' | 'json' | 'text';
  context?: HttpContext;
  successNotification?: {
    enabled?: boolean;
    title?: string;
    message?: string;
  };
  errorNotification?: {
    enabled?: boolean;
  };
}

@Injectable({ providedIn: 'root' })
export class HttpClientService {
  private readonly http = inject(HttpClient);

  get<T>(url: string, options: HttpRequestOptions = {}): Observable<T> {
    return this.http.get(url, this.withNotificationContext(options) as never) as Observable<T>;
  }

  post<T>(url: string, body: unknown, options: HttpRequestOptions = {}): Observable<T> {
    return this.http.post(
      url,
      body,
      this.withNotificationContext(options) as never,
    ) as Observable<T>;
  }

  patch<T>(url: string, body: unknown, options: HttpRequestOptions = {}): Observable<T> {
    return this.http.patch(
      url,
      body,
      this.withNotificationContext(options) as never,
    ) as Observable<T>;
  }

  put<T>(url: string, body: unknown, options: HttpRequestOptions = {}): Observable<T> {
    return this.http.put(
      url,
      body,
      this.withNotificationContext(options) as never,
    ) as Observable<T>;
  }

  delete<T>(url: string, options: HttpRequestOptions = {}): Observable<T> {
    return this.http.delete(url, this.withNotificationContext(options) as never) as Observable<T>;
  }

  private withNotificationContext(options: HttpRequestOptions): HttpRequestOptions {
    const configuredNotification = options.successNotification;
    const configuredErrorNotification = options.errorNotification;
    if (!configuredNotification && !configuredErrorNotification) {
      return options;
    }

    let context = options.context ?? new HttpContext();
    if (configuredNotification) {
      context = context.set(SUCCESS_NOTIFICATION_OPTIONS, {
        enabled: configuredNotification.enabled ?? false,
        title: configuredNotification.title,
        message: configuredNotification.message,
      });
    }
    if (configuredErrorNotification) {
      context = context.set(
        ERROR_NOTIFICATION_ENABLED,
        configuredErrorNotification.enabled ?? true,
      );
    }

    const {
      successNotification: _successNotification,
      errorNotification: _errorNotification,
      ...requestOptions
    } = options;
    return {
      ...requestOptions,
      context,
    };
  }
}

export const httpClientInterceptor: HttpInterceptorFn = (req, next) => {
  const notificationService = inject(NotificationService);
  const authSession = inject(AuthSessionService);
  const userDetailsService = inject(UserDetailsService);
  const dashboardTypesService = inject(DashboardTypesService);
  const powerBiConfig = resolvePowerBiConfig(dashboardTypesService.selectedDashboard());
  const routedUrl = mapToAbsoluteApiUrl(req.url, powerBiConfig?.baseUrl ?? '');
  const isPowerBiApiRequest = isPowerBiRequest(req.url, powerBiConfig);

  return from(authSession.getAccessToken()).pipe(
    switchMap((accessToken) => {
      let headers = req.headers;
      const requiresBearerAuth = shouldAttachAuthorizationHeader(req.url, powerBiConfig);
      // Keep multipart uploads valid by letting the browser set Content-Type + boundary.
      if (req.body instanceof FormData && headers.has('Content-Type')) {
        headers = headers.delete('Content-Type');
      }

      // const apiRequest = isApiRequest(req.url);
      if (isServiceNowRequest(req.url)) {
        // Keep binary attachment responses intact for blob requests.
        if (req.responseType !== 'blob' && !headers.has('Accept')) {
          headers = headers.set('Accept', 'application/json');
        }
      }

      if (isPowerBiApiRequest) {
        if (hasRequestBody(req.method) && !headers.has('Content-Type')) {
          headers = headers.set('Content-Type', 'application/json');
        }

        if (powerBiConfig?.subscriptionKey && !headers.has('ocp-apim-subscription-key')) {
          headers = headers.set('ocp-apim-subscription-key', powerBiConfig.subscriptionKey);
        }
      }

      if (shouldAttachCockpitUserHeaders(routedUrl)) {
        const userName = readHeaderText(userDetailsService.userDetails()?.['user_name']);
        const userSysId = userDetailsService.userSysId().trim();

        headers = headers.set('X-User-Email', userName).set('X-User-Sysid', userSysId);
      }

      // const oktaAuthorizationHeader = resolveOktaAuthorizationHeader();
      // if (oktaAuthorizationHeader && !headers.has('Authorization')) {
      //   headers = headers.set('Authorization', oktaAuthorizationHeader);
      // }

      if (requiresBearerAuth && !accessToken) {
        return throwError(
          () =>
            new HttpErrorResponse({
              status: 401,
              statusText: 'Unauthorized',
              error: 'Missing access token for API request.',
              url: routedUrl,
            }),
        );
      }
       if(routedUrl.includes('apigw.isg-one.com')){
        const bypassReq = req.clone({
            url: routedUrl,
        headers,
        })
        return next(bypassReq).pipe(
        tap((event) => {
          if (!(event instanceof HttpResponse)) {
            return;
          }

          const successNotification = req.context.get(SUCCESS_NOTIFICATION_OPTIONS);
          if (!successNotification.enabled) {
            return;
          }

          if (isFailedResultResponse(event.body)) {
            notificationService.showErrorMessage(resolveFailureMessage(event.body), 'Error!');
            return;
          }

          notificationService.showSuccessMessage(
            successNotification.message ?? resolveSuccessMessage(event.body),
            successNotification.title,
          );
        }),
        catchError((error: HttpErrorResponse) => {
          if (!req.context.get(ERROR_NOTIFICATION_ENABLED)) {
            return throwError(() => error);
          }

          return handleHttpInterceptorError(error, notificationService);
        }),
      )
      }
      const token = JSON.parse(localStorage['okta-token-storage']).accessToken.accessToken;
      if (token) {
        headers = headers.set('Authorization', `Bearer ${token}`);
      } else {
        alert('no okta token');
      }

      const apiReq = req.clone({
        url: routedUrl,
        headers,
      });
     
      if(!apiReq.url.includes('api/proxy')){
        const serviceNowBase = environment.endpoints.serviceNow;

        const normalizedUrl = apiReq.url.replace(
          /^https?:\/\/[^/]+/,
          serviceNowBase+'/api/proxy/ForwardGXAPI'
        );
        const norReq = req.clone({
          url: normalizedUrl,
          headers
        })
        return next(norReq).pipe(
        tap((event) => {
          if (!(event instanceof HttpResponse)) {
            return;
          }

          const successNotification = req.context.get(SUCCESS_NOTIFICATION_OPTIONS);
          if (!successNotification.enabled) {
            return;
          }

          if (isFailedResultResponse(event.body)) {
            notificationService.showErrorMessage(resolveFailureMessage(event.body), 'Error!');
            return;
          }

          notificationService.showSuccessMessage(
            successNotification.message ?? resolveSuccessMessage(event.body),
            successNotification.title,
          );
        }),
        catchError((error: HttpErrorResponse) => {
          if (!req.context.get(ERROR_NOTIFICATION_ENABLED)) {
            return throwError(() => error);
          }

          return handleHttpInterceptorError(error, notificationService);
        }),
      )
      }
      return next(apiReq).pipe(
        tap((event) => {
          if (!(event instanceof HttpResponse)) {
            return;
          }

          const successNotification = req.context.get(SUCCESS_NOTIFICATION_OPTIONS);
          if (!successNotification.enabled) {
            return;
          }

          if (isFailedResultResponse(event.body)) {
            notificationService.showErrorMessage(resolveFailureMessage(event.body), 'Error!');
            return;
          }

          notificationService.showSuccessMessage(
            successNotification.message ?? resolveSuccessMessage(event.body),
            successNotification.title,
          );
        }),
        catchError((error: HttpErrorResponse) => {
          if (!req.context.get(ERROR_NOTIFICATION_ENABLED)) {
            return throwError(() => error);
          }

          return handleHttpInterceptorError(error, notificationService);
        }),
      );
    }),
  );
};

function handleHttpInterceptorError(
  error: HttpErrorResponse,
  notificationService: NotificationService,
): Observable<never> {
  switch (error.status) {
    case 0:
      console.error('Network Error');
      break;
    case 400:
      notificationService.showErrorMessage(resolveBadRequestMessage(error), 'Bad Request!');
      console.error('Bad Request', error.error);
      break;
    case 401:
      notificationService.showErrorMessage(resolveBadRequestMessage(error), 'Unauthorized!');
      console.error('Unauthorized');
      break;
    case 403:
      notificationService.showErrorMessage(resolveBadRequestMessage(error), 'Forbidden!');
      console.error('Forbidden');
      break;
    case 404:
      notificationService.showErrorMessage(resolveBadRequestMessage(error), 'Not Found!');
      console.error('Not Found');
      break;
    case 500:
      notificationService.showErrorMessage(
        resolveBadRequestMessage(error),
        'Internal Server Error!',
      );
      console.error('Internal Server Error');
      break;
    case 409:
      notificationService.showErrorMessage(resolveBadRequestMessage(error), 'Conflict!');
      console.error('Conflict');
      break;
    default:
      notificationService.showErrorMessage(resolveBadRequestMessage(error), 'Error!');
      console.error('Unexpected Error', error);
  }

  return throwError(() => error);
}

function resolveBadRequestMessage(error: HttpErrorResponse): string {
  const rawError = error?.error;
  const errorBody =
    rawError && typeof rawError === 'object' && 'result' in rawError
      ? (rawError as { result: unknown }).result
      : rawError;

  if (typeof errorBody === 'string' && errorBody.trim()) {
    return errorBody;
  }

  if (errorBody && typeof errorBody === 'object') {
    const message = (errorBody as { message?: unknown }).message;
    if (typeof message === 'string' && message.trim()) {
      return message;
    }

    const detail = (errorBody as { detail?: unknown }).detail;
    if (typeof detail === 'string' && detail.trim()) {
      return detail;
    }
  }

  return APP_MESSAGES.general.VERIFY_REQUEST_RETRY_MESSAGE;
}

function mapToAbsoluteApiUrl(url: string, powerBiBaseUrl: string): string {
  if (isAbsoluteUrl(url)) {
    return url;
  }

  if (url.startsWith(API_ROUTE_PREFIXES.serviceNow)) {
    return joinUrl(
      environment.endpoints.serviceNow,
      url.slice(API_ROUTE_PREFIXES.serviceNow.length),
    );
  }

  if (url.startsWith(API_ROUTE_PREFIXES.serviceNowDirect)) {
    return joinUrl(environment.endpoints.serviceNow, url);
  }

  if (url.startsWith(API_ROUTE_PREFIXES.powerBi)) {
    if (!powerBiBaseUrl.trim()) {
      return url;
    }

    return resolvePowerBiUrl(powerBiBaseUrl, url.slice(API_ROUTE_PREFIXES.powerBi.length));
  }
  if (url.startsWith(API_ROUTE_PREFIXES.proxy)) {
  return joinUrl(
    environment.endpoints.serviceNow,
    url,
  );
}

  return url;
}

function resolvePowerBiUrl(baseUrl: string, suffix: string): string {
  const normalizedBaseUrl = normalizePowerBiBaseUrl(baseUrl);
  const normalizedSuffix = suffix.trim().toLowerCase();

  if (!normalizedSuffix || normalizedSuffix === '/') {
    return normalizedBaseUrl;
  }

  if (normalizedSuffix === '/export') {
    return toPowerBiExportUrl(normalizedBaseUrl);
  }

  return joinUrl(normalizedBaseUrl, suffix);
}

function normalizePowerBiBaseUrl(baseUrl: string): string {
  const trimmed = baseUrl.trim();
  if (!trimmed) {
    return trimmed;
  }

  return trimmed.endsWith('/') ? trimmed.slice(0, -1) : trimmed;
}

function toPowerBiExportUrl(chartUrl: string): string {
  const normalizedUrl = normalizePowerBiBaseUrl(chartUrl);

  try {
    const parsed = new URL(normalizedUrl);
    const segments = parsed.pathname.split('/').filter(Boolean);

    if (segments.length === 0) {
      return joinUrl(normalizedUrl, 'export');
    }

    const lastIndex = segments.length - 1;
    if (segments[lastIndex].toLowerCase() === 'chart') {
      segments[lastIndex] = 'Export';
    } else {
      segments.push('export');
    }

    parsed.pathname = `/${segments.join('/')}`;
    return normalizePowerBiBaseUrl(parsed.toString());
  } catch {
    if (/\/chart$/i.test(normalizedUrl)) {
      return normalizedUrl.replace(/\/chart$/i, '/Export');
    }

    return joinUrl(normalizedUrl, 'export');
  }
}

function resolvePowerBiConfig(dashboard: DashboardTypeItem | null): PowerBiRequestConfig | null {
  if (!dashboard) {
    return null;
  }

  const baseUrl = dashboard.u_env.trim();
  const subscriptionKey = dashboard.u_key.trim();

  if (!baseUrl || !subscriptionKey) {
    return null;
  }

  return {
    baseUrl,
    exportUrl: toPowerBiExportUrl(baseUrl),
    subscriptionKey,
  };
}

function joinUrl(baseUrl: string, path: string): string {
  const normalizedBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${normalizedBaseUrl}${normalizedPath}`;
}

function isAbsoluteUrl(url: string): boolean {
  return /^https?:\/\//i.test(url);
}

function isServiceNowRequest(url: string): boolean {
  return SERVICE_NOW_ROUTE_PREFIXES.some((prefix) => url.startsWith(prefix));
}

function isPowerBiRequest(url: string, config: PowerBiRequestConfig | null): boolean {
  if (url.startsWith(API_ROUTE_PREFIXES.powerBi)) {
    return true;
  }

  if (!isAbsoluteUrl(url) || !config) {
    return false;
  }

  const normalizedUrl = normalizePowerBiBaseUrl(url);
  const chartUrl = normalizePowerBiBaseUrl(config.baseUrl);
  const exportUrl = normalizePowerBiBaseUrl(config.exportUrl);
  return normalizedUrl === chartUrl || normalizedUrl === exportUrl;
}

function shouldAttachCockpitUserHeaders(url: string): boolean {
  return COCKPIT_USER_HEADER_PATHS.some((path) => url.includes(path));
}

function readHeaderText(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

// function isApiRequest(url: string): boolean {
//   return isServiceNowRequest(url) || isPowerBiRequest(url);
// }

function shouldAttachAuthorizationHeader(
  url: string,
  config: PowerBiRequestConfig | null,
): boolean {
  return isPowerBiRequest(url, config);
}

function resolveOktaAuthorizationHeader(): string | null {
  const basicToken = environment.oktaAuth?.basicToken?.trim();
  if (basicToken) {
    return /^basic\s+/i.test(basicToken) ? basicToken : `Basic ${basicToken}`;
  }

  const username = environment.oktaAuth?.username?.trim();
  const password = environment.oktaAuth?.password;
  if (!username || !password) {
    return null;
  }

  return `Basic ${encodeBase64(`${username}:${password}`)}`;
}

function encodeBase64(value: string): string {
  const bytes = new TextEncoder().encode(value);
  let binary = '';
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary);
}

function hasRequestBody(method: string): boolean {
  return ['POST', 'PUT', 'PATCH'].includes(method.toUpperCase());
}

function resolveSuccessMessage(responseBody: unknown): string {
  if (responseBody && typeof responseBody === 'object') {
    const message = (responseBody as { message?: unknown }).message;
    if (typeof message === 'string' && message.trim()) {
      return message;
    }

    const detail = (responseBody as { detail?: unknown }).detail;
    if (typeof detail === 'string' && detail.trim()) {
      return detail;
    }
  }

  return 'Request completed successfully.';
}

function isFailedResultResponse(responseBody: unknown): boolean {
  if (!isPlainObject(responseBody)) {
    return false;
  }

  if (isFailedStatusValue(responseBody['status'])) {
    return true;
  }

  const result = responseBody['result'];
  return isPlainObject(result) && isFailedStatusValue(result['status']);
}

function isFailedStatusValue(value: unknown): boolean {
  return typeof value === 'string' && value.trim().toLowerCase() === 'failed';
}

function resolveFailureMessage(responseBody: unknown): string {
  const directMessage = firstFailureMessage(responseBody);
  if (directMessage) {
    return directMessage;
  }

  if (isPlainObject(responseBody)) {
    const nestedResultMessage = firstFailureMessage(responseBody['result']);
    if (nestedResultMessage) {
      return nestedResultMessage;
    }
  }

  return 'Request failed. Please try again.';
}

function firstFailureMessage(value: unknown): string | null {
  if (!isPlainObject(value)) {
    return null;
  }

  const message = value['message'];
  if (typeof message === 'string' && message.trim()) {
    return message;
  }

  const detail = value['detail'];
  if (typeof detail === 'string' && detail.trim()) {
    return detail;
  }

  const results = value['results'];
  if (!Array.isArray(results)) {
    return null;
  }

  const failedResultMessage = results
    .filter((item): item is Record<string, unknown> => isPlainObject(item))
    .find((item) => isFailedStatusValue(item['status']) && typeof item['message'] === 'string')?.[
    'message'
  ];

  if (typeof failedResultMessage === 'string' && failedResultMessage.trim()) {
    return failedResultMessage;
  }

  const firstResultMessage = results
    .filter((item): item is Record<string, unknown> => isPlainObject(item))
    .find((item) => typeof item['message'] === 'string')?.['message'];

  return typeof firstResultMessage === 'string' && firstResultMessage.trim()
    ? firstResultMessage
    : null;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function shouldBypassProxy(url: string): boolean {
  return (
    url.startsWith('data/') ||
    url.startsWith('/data/') ||
    url.startsWith('assets/') ||
    url.startsWith('/assets/') ||
    url.endsWith('.json')
  );
}
